import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { X, PawPrint } from "lucide-react";
import { track } from "../lib/analytics";

/**
 * Signup prompt modal — shown when a logged-out user tries to perform
 * a gated action (save, check-in, review, busy report, see activity).
 *
 * Props:
 *   - open: boolean — controls visibility
 *   - onClose: () => void — fired when user dismisses (overlay click,
 *              Esc, X button, or "Maybe later")
 *   - trigger: one of "save" | "checkin" | "review" | "activity" |
 *              "busy_report" — drives the body copy AND the GA4
 *              event parameter
 *
 * Fires `signup_prompt_shown` with `trigger` on mount-open transition.
 */
const COPY = {
  save: {
    heading: "Save your favorite spots",
    body: "Want to save this spot? Create a free account to build your favorites list 🐾",
  },
  activity: {
    heading: "Dogs are here right now!",
    body: "Sign up to see who's checked in and join them 🐾",
  },
  checkin: {
    heading: "Let the pack know you're here",
    body: "Check in to let other pet parents know you're here! Create a free account to get started 🐾",
  },
  review: {
    heading: "Share your experience",
    body: "Been here before? Sign up to leave a review and help other pet parents find great spots 🐾",
  },
  busy_report: {
    heading: "Help the community",
    body: "Sign up to report how busy this place is right now 🐾",
  },
  suggest_place: {
    heading: "Sign up to suggest a dog-friendly place!",
    body: "Pawground only takes suggestions from registered dog parents — it keeps the listings authentic 🐾",
  },
  // Shown when an already-logged-in user with no dog profile tries to
  // check in. CTA points at /onboarding (not /signup) and the dismiss
  // ("Maybe Later") button is suppressed below — a dog profile is
  // required to check in, so there's no soft path out of this prompt.
  needs_dog_profile: {
    heading: "Add your pup to check in 🐾",
    body: "It only takes 30 seconds — just your dog's name, breed, and size.",
    cta: "Add My Dog",
    ctaHref: "/onboarding",
    hideDismiss: true,
    hideMicrocopy: true,
    hideLoginFooter: true,
  },
};

const SignupPromptModal = ({ open, onClose, trigger = "save" }) => {
  const location = useLocation();
  const copy = COPY[trigger] || COPY.save;

  // Fire the GA4 event exactly once per open transition. The empty-deps
  // pattern won't work here because `trigger` may change between opens
  // (same modal, different action). Effect re-runs on open=true and
  // skips when open=false so we don't spam GA on close.
  useEffect(() => {
    if (!open) return;
    track("signup_prompt_shown", { trigger });
  }, [open, trigger]);

  // Lock body scroll while the modal is open — without this, scrolling
  // the modal scrolls the page underneath on iOS.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc-to-close. Mirrors radix Dialog semantics without pulling in the
  // full primitive — keeps the modal lightweight + brand-styled.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const redirect = location.pathname + (location.search || "");
  const signupHref = `/signup?redirect=${encodeURIComponent(redirect)}`;

  return (
    <div
      data-testid="signup-prompt-modal"
      data-trigger={trigger}
      role="dialog"
      aria-modal="true"
      aria-labelledby="signup-prompt-heading"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
    >
      {/* Overlay */}
      <button
        type="button"
        aria-label="Close"
        data-testid="signup-prompt-overlay"
        onClick={onClose}
        className="absolute inset-0 bg-black/55 backdrop-blur-sm animate-in fade-in-0"
      />

      {/* Card */}
      <div className="relative w-full max-w-sm bg-brand-cream rounded-3xl shadow-warm-lg border border-brand-border-warm p-6 sm:p-7 animate-in fade-in-0 zoom-in-95 duration-150">
        <button
          type="button"
          aria-label="Close signup prompt"
          data-testid="signup-prompt-close"
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-brand-ink/55 hover:text-brand-ink hover:bg-brand-border-warm/40 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand-forest/10 mb-4">
          <PawPrint className="w-6 h-6 text-brand-forest" />
        </div>

        <h2
          id="signup-prompt-heading"
          data-testid="signup-prompt-heading"
          className="font-heading text-2xl font-bold text-brand-ink leading-tight"
        >
          {copy.heading}
        </h2>
        <p
          data-testid="signup-prompt-body"
          className="text-[15px] text-brand-ink/75 leading-relaxed mt-2"
        >
          {copy.body}
        </p>

        <div className="mt-6 flex flex-col gap-2">
          <Link
            to={copy.ctaHref ? copy.ctaHref : signupHref}
            data-testid="signup-prompt-cta"
            onClick={() => track("signup_prompt_cta_clicked", { trigger })}
            className="bg-brand-forest hover:bg-brand-forest-dark text-white font-semibold rounded-full px-5 py-3 text-sm text-center transition-colors active:scale-[0.98]"
          >
            {copy.cta || "Create Free Account"}
          </Link>
          {/* Reassurance microcopy — addresses the "is this worth signing
              up for?" objection right at the moment of intent. Muted
              olive-grey (#7A7A60) keeps it visually quiet under the CTA.
              Hidden for hard-required triggers (e.g. needs_dog_profile)
              where it'd just feel like clutter. */}
          {!copy.hideMicrocopy && (
            <p
              data-testid="signup-prompt-microcopy"
              className="text-xs text-center font-sans"
              style={{ color: "#7A7A60" }}
            >
              100% free to join
            </p>
          )}
          {/* "Maybe Later" — suppressed on hard-required triggers like
              needs_dog_profile where dismissing has no productive path.
              The overlay/close button still works, but the soft dismiss
              CTA disappears. */}
          {!copy.hideDismiss && (
            <button
              type="button"
              data-testid="signup-prompt-dismiss"
              onClick={onClose}
              className="text-brand-ink/70 hover:text-brand-ink font-semibold text-sm px-5 py-2.5 rounded-full transition-colors"
            >
              Maybe Later
            </button>
          )}
        </div>

        {!copy.hideLoginFooter && (
          <p className="text-xs text-brand-ink/55 text-center mt-4">
            Already have an account?{" "}
            <Link
              to={`/login?redirect=${encodeURIComponent(redirect)}`}
              data-testid="signup-prompt-login-link"
              // rel="nofollow" — this is the worst offender for the
              // SEO incident: every /park/<id> the modal pops over
              // generates a unique /login?redirect=/park/<id> link
              // that Google was crawling as a separate URL. Nofollow
              // tells crawlers to skip it; robots.txt Disallow /login
              // is the second line of defense for ones already in the
              // index.
              rel="nofollow"
              className="text-brand-forest font-semibold hover:underline"
            >
              Log in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default SignupPromptModal;
